import { Stack } from "@mui/material";
import { useBankingAccounts } from "Api/Queries/Accounts/useBankingAccounts";
import { useTransactionsDetail } from "Api/Queries/Transactions/useTransactionsDetail";
import { useUserInfoQuery } from "Auth/Api/Queries/useUserInfoQuery";
import { StandardPaymentForm } from "Components/Customer/Payment/PaymentForms/StandardPaymentForm";
import { QuickPaymentComponent } from "Components/Customer/Payment/QuickPaymentComponent";
import { MooneLitePopup } from "Components/Dashboard/MooneLitePopup";
import { CenteredCircularProgress } from "Components/Shared/CenteredCircularProgress";
import { InfoBox } from "Components/Shared/InfoBox/InfoBox";
import { PrimaryPaper } from "Components/Shared/PrimaryPaper";
import { Resources, useResource } from "Translations/Resources";
import { pickMainBankAccount } from "Utils/AccountUtils";
import { getQuickPaymentAttemptedKey } from "Utils/Storage/LocalStorage/LocalStorageUtils";
import {
  getLocalStorageItem,
  isLocalStorageAvailable,
} from "Utils/Storage/LocalStorage/SecureLocalStorage";

const QuickPaymentResources = Resources.Customer.Pay.QuickPayment;
const ErrorResources = Resources.TransactionErrors;

type Props = {
  publicID?: string | null;
  transactionPublicID?: string | null;
};

export const CustomerPaymentPayComponent = ({
  publicID,
  transactionPublicID,
}: Props) => {
  const { t } = useResource();
  const { data: userInfo, isLoading: isLoadingUserInfo } = useUserInfoQuery();
  const { data, isLoading: isLoadingBankAccounts } = useBankingAccounts();
  const {
    data: transactionDetails,
    isLoading: isLoadingTransactionDetails,
    isError: isTransactionLoadError,
  } = useTransactionsDetail({ publicID: transactionPublicID });

  const isLoading =
    isLoadingUserInfo || isLoadingBankAccounts || isLoadingTransactionDetails;

  if (isLoading) {
    return (
      <PrimaryPaper sx={{ height: ["100%", "auto"] }}>
        <CenteredCircularProgress />
      </PrimaryPaper>
    );
  }

  if (!data || !userInfo) {
    return (
      <PrimaryPaper sx={{ height: ["100%", "auto"] }}>
        {t(Resources.Errors.RequestError)}
      </PrimaryPaper>
    );
  }

  if (isTransactionLoadError || !transactionDetails) {
    return (
      <PrimaryPaper sx={{ height: ["100%", "auto"] }}>
        {t(ErrorResources.TransactionNotFound)}
      </PrimaryPaper>
    );
  }

  const bankAccounts = data.items;
  // Primary is per currency — quick payment debits the primary of the TRANSACTION's
  // currency (mirrors the backend PayTransactionQuickCommand selection).
  const primaryAccountEnvironment = bankAccounts.find(
    ({ isPrimary, currency }) =>
      isPrimary && currency === transactionDetails.currencyCode,
  )?.environment;

  const wasQuickPaymentAttempted =
    transactionPublicID &&
    getLocalStorageItem(getQuickPaymentAttemptedKey(transactionPublicID)) ===
      "true";

  // A tip-enabled pay point must show the detail screen so the customer can pick a tip —
  // quick pay would settle the bill with no tip (ZS-1674). Evaluated before QuickPaymentComponent
  // mounts, so the one-shot "attempted" flag is never written and no failure banner appears.
  const isQuickPaymentBlockedByTips = transactionDetails.payPointTipsEnabled;

  const isQuickPaymentAvailable =
    isLocalStorageAvailable() &&
    !wasQuickPaymentAttempted &&
    !isQuickPaymentBlockedByTips &&
    userInfo.isQuickPaymentEnabled &&
    primaryAccountEnvironment &&
    transactionPublicID;

  if (isQuickPaymentAvailable) {
    return (
      <QuickPaymentComponent
        environment={primaryAccountEnvironment}
        transactionPublicID={transactionPublicID}
      />
    );
  }

  const bankAccountByUrlParam =
    publicID && bankAccounts.find((account) => account.publicID === publicID);
  const selectedBankAccount =
    bankAccountByUrlParam ||
    pickMainBankAccount(bankAccounts, transactionDetails.currencyCode);

  const defaultValues = {
    publicID: transactionPublicID ?? undefined,
    bankAccountPublicID: selectedBankAccount?.publicID,
  };

  return (
    <Stack gap={2} height="100%">
      {wasQuickPaymentAttempted && (
        <InfoBox
          variant="warning"
          content={t(QuickPaymentResources.QuickPaymentFailed)}
        />
      )}
      <StandardPaymentForm
        transactionDetails={transactionDetails}
        bankAccounts={bankAccounts}
        defaultValues={defaultValues}
      />
      <MooneLitePopup />
    </Stack>
  );
};
